/**
 * Form Field Renderer Component for dev-tools
 * Part of Phase 3 refactoring - Component Decomposition (UI Components)
 *
 * Renders form fields based on schema definitions
 */
import React from "react";

import { Box, Typography } from "@mui/material";

import { FieldSchema, FormState } from "../types";
import { InputFactory } from "./input-factory";

interface FormFieldRendererProps {
  fields: FieldSchema[];
  formState: FormState;
  onFieldChange: (fieldName: string, value: unknown) => void;
}

export const FormFieldRenderer: React.FC<FormFieldRendererProps> = ({
  fields,
  formState,
  onFieldChange,
}) => {
  if (fields.length === 0) {
    return (
      <Box
        sx={{
          py: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: 50,
            height: 50,
            borderRadius: "50%",
            bgcolor: "action.hover",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
          }}
        >
          <Typography variant="h5" color="text.secondary">
            📝
          </Typography>
        </Box>
        <Typography variant="body1" color="text.primary" gutterBottom>
          No Fields Available
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This method doesn&apos;t require any input parameters.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {fields.map((field) => {
        const fieldValue = formState.data[field.name];
        return (
          <Box key={field.name}>
            <InputFactory
              field={field}
              value={fieldValue}
              onChange={onFieldChange}
              error={formState.errors[field.name]}
              touched={formState.touched[field.name]}
            />
          </Box>
        );
      })}
    </Box>
  );
};
